import HttpFactory from "~/repository/factory";
import type {
  SchoolStore,
  SchoolStoreInput,
  StoreAsset,
  StoreCategory,
  StoreCategoryInput,
  StoreConnection,
  StoreConnectionInput,
  StoreFilters,
  StoreFulfillmentInput,
  StoreOrder,
  StorePage,
  StoreProduct,
  StoreProductInput,
  StoreProductImage,
} from "~/types/schoolStore";

const base = "/api/organizations/current/store/";

export default class SchoolStoreModule extends HttpFactory {
  getStore() {
    return this.get<SchoolStore>(base);
  }

  updateStore(
    data: (Partial<SchoolStoreInput> & { banner?: null }) | FormData,
  ) {
    return this.patch<SchoolStore>(base, data);
  }

  getConnection() {
    return this.get<StoreConnection>(`${base}connection/`);
  }

  updateConnection(data: StoreConnectionInput) {
    return this.patch<StoreConnection>(`${base}connection/`, data);
  }

  validateConnection() {
    return this.post<StoreConnection>(`${base}connection/validate/`, {});
  }

  rotateWebhook() {
    return this.post<StoreConnection>(`${base}connection/rotate-webhook/`, {});
  }

  listCategories(filters: StoreFilters = {}) {
    return this.get<StoreCategory[]>(`${base}categories/`, filters);
  }

  createCategory(data: StoreCategoryInput) {
    return this.post<StoreCategory>(`${base}categories/`, data);
  }

  updateCategory(id: string, data: Partial<StoreCategoryInput>) {
    return this.patch<StoreCategory>(
      `${base}categories/${encodeURIComponent(id)}/`,
      data,
    );
  }

  deleteCategory(id: string) {
    return this.delete<void>(`${base}categories/${encodeURIComponent(id)}/`);
  }

  listProducts(filters: StoreFilters = {}) {
    return this.get<StoreProduct[]>(`${base}products/`, filters);
  }

  getProduct(id: string) {
    return this.get<StoreProduct>(`${base}products/${encodeURIComponent(id)}/`);
  }

  createProduct(data: StoreProductInput) {
    return this.post<StoreProduct>(`${base}products/`, data);
  }

  updateProduct(id: string, data: Partial<StoreProductInput> | FormData) {
    return this.patch<StoreProduct>(
      `${base}products/${encodeURIComponent(id)}/`,
      data,
    );
  }

  deleteProduct(id: string) {
    return this.delete<void>(`${base}products/${encodeURIComponent(id)}/`);
  }

  adjustStock(id: string, quantity: number, reason: string) {
    return this.post<StoreProduct>(
      `${base}products/${encodeURIComponent(id)}/stock/`,
      { quantity, reason },
    );
  }

  listAssets(id: string) {
    return this.get<StoreAsset[]>(
      `${base}products/${encodeURIComponent(id)}/assets/`,
    );
  }

  uploadAsset(id: string, file: File) {
    const data = new FormData();
    data.append("file", file);
    return this.post<StoreAsset>(
      `${base}products/${encodeURIComponent(id)}/assets/`,
      data,
    );
  }

  uploadImage(id: string, file: File) {
    const data = new FormData();
    data.append("file", file);
    return this.post<StoreProductImage>(
      `${base}products/${encodeURIComponent(id)}/images/`,
      data,
    );
  }

  deleteImage(productId: string, imageId: string) {
    return this.delete<void>(
      `${base}products/${encodeURIComponent(productId)}/images/${encodeURIComponent(imageId)}/`,
    );
  }

  listOrders(filters: StoreFilters = {}) {
    return this.get<StorePage<StoreOrder>>(`${base}orders/`, filters);
  }

  getOrder(id: string) {
    return this.get<StoreOrder>(`${base}orders/${encodeURIComponent(id)}/`);
  }

  fulfillOrder(id: string, data: StoreFulfillmentInput) {
    return this.patch<StoreOrder>(
      `${base}orders/${encodeURIComponent(id)}/fulfillment/`,
      data,
    );
  }

  reconcileOrder(id: string) {
    return this.post<StoreOrder>(
      `${base}orders/${encodeURIComponent(id)}/reconcile/`,
      {},
    );
  }
}
